import { QBankStructureLevelCategory } from '@exam-management/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import type { PropsWithChildren } from '@shared/utils';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { useAddSingleQuestionConditions } from '../add-single-question-conditions-provider';

import {
  AddSingleQuestionConditionsSchema,
  createQuestionTypeSchemaByQuestionCount,
  type AddSingleQuestionConditionsFormType,
} from './add-single-question-conditions-schema';

interface AddSingleQuestionConditionsFormProviderProps extends PropsWithChildren {
  initialValues?: AddSingleQuestionConditionsFormType;
}

export default function AddSingleQuestionConditionsFormProvider(
  props: Readonly<AddSingleQuestionConditionsFormProviderProps>,
) {
  const { children, initialValues } = props;

  const { chapterData, levelItemsWithLevel, checkExistOnStructures, questionCount } =
    useAddSingleQuestionConditions();

  const formContext = useForm<AddSingleQuestionConditionsFormType>({
    defaultValues: initialValues ?? {
      questionType: {
        calculated: {
          hard: null,
          medium: null,
          easy: null,
        },
        notCalculated: { hard: null, medium: null, easy: null },
      },
    },
    resolver: zodResolver(
      AddSingleQuestionConditionsSchema.extend({
        questionType: createQuestionTypeSchemaByQuestionCount(questionCount.get),
      }),
    ),
    mode: 'onChange',
  });

  const { watch, reset, getValues } = formContext;

  const { contentType } = watch();

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'contentType') {
        reset({
          ...getValues(),
          los: { label: '', value: '', inputValue: null },
          location: { topics: {}, subtopics: {} },
          questionType: {
            calculated: {
              hard: null,
              medium: null,
              easy: null,
            },
            notCalculated: { hard: null, medium: null, easy: null },
          },
        });
      }
      if (name === 'los') {
        reset({
          ...getValues(),
          questionType: {
            calculated: {
              hard: null,
              medium: null,
              easy: null,
            },
            notCalculated: { hard: null, medium: null, easy: null },
          },
        });
      }
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (levelItemsWithLevel.length) {
      let topicLevelItems = levelItemsWithLevel.filter(
        (item) => item.level.category === QBankStructureLevelCategory.Topic,
      );
      if (topicLevelItems.length === 0) {
        topicLevelItems = levelItemsWithLevel.filter(
          (item) => item.level.category === QBankStructureLevelCategory.Chapter,
        );
      }

      const subTopicLevelItems = levelItemsWithLevel.filter(
        (item) => item.level.category === QBankStructureLevelCategory.SubTopic,
      );

      // check all subtopics
      const topics: Record<string, boolean> = topicLevelItems.reduce((acc, item) => {
        const children = subTopicLevelItems.filter(
          (subItem) => subItem.parentLevelItemId === item.id,
        );
        if (initialValues?.location.topics[item.id] === true) {
          return {
            ...acc,
            [item.id]: true,
          };
        }
        const isAllSelected = children.every((child) => checkExistOnStructures(child.id));
        return {
          ...acc,
          [item.id]: isAllSelected ? undefined : false,
        };
      }, {});

      if (!topicLevelItems.length && chapterData?.refId) {
        topics[chapterData.refId] = subTopicLevelItems
          .filter((subItem) => subItem.parentLevelItemId === chapterData.refId)
          .every((child) => checkExistOnStructures(child.id));
      }

      //  check selected
      const subTopics = subTopicLevelItems.reduce((acc, item) => {
        const isAlreadySelected = checkExistOnStructures(item.id);
        if (initialValues?.location.subtopics[item.id] === true) {
          return {
            ...acc,
            [item.id]: true,
          };
        }
        return {
          ...acc,
          [item.id]: isAlreadySelected ? undefined : false,
        };
      }, {});

      formContext.reset({
        ...formContext.getValues(),
        location: {
          topics,
          subtopics: subTopics,
        },
      });
    }
  }, [levelItemsWithLevel, contentType]);

  return <FormProvider {...formContext}>{children}</FormProvider>;
}
